import { type Theme } from "@mui/material";
import { type Components } from "@mui/material/styles";

import { type TabVariant } from "../types";

declare module "@mui/material/Tab" {
  interface TabOwnProps {
    // Optional because it's used outside of redesign components
    appearance?: TabVariant;
  }
}

declare module "@mui/material/Tabs" {
  interface TabsOwnProps {
    // Optional because it's used outside of redesign components
    appearance?: TabVariant;
  }
}

export const muiTabsOverrides: Components<Theme> = {
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        maxWidth: "100%", // Constrain to parent width
        overflowX: "auto", // Enable horizontal scrolling when needed
        height: theme.size?.tabs.height,
        width: "fit-content",
        "&::-webkit-scrollbar": {
          display: "none", // Hide scrollbar for WebKit browsers
        },
        scrollbarWidth: "none", // Hide scrollbar for Firefox
        "-ms-overflow-style": "none", // Hide scrollbar for IE/Edge
        "& .MuiTabs-scroller": {
          overflow: "visible !important",
        },
        alignItems: "center",
        display: "flex",
        variants: [
          {
            props: { appearance: "pill" },
            style: {
              border: "none",

              padding: theme.spacing(1),

              "& .MuiTab-root": {
                marginRight: theme.spacing(2),
              },
              "& .MuiTabs-indicator": {
                transition: "none",
                height: theme.size?.tabs.height,
              },
              "& .MuiTabs-flexContainer": {
                gap: theme.spacing(2),
              },
            },
          },
          {
            props: { appearance: "underlined" },
            style: {
              width: "100%",

              "& .MuiTabs-flexContainer": {
                width: "100%",
              },
              "& .MuiTabs-scroller": {
                height: theme.size?.tabs.height,
                boxShadow: `inset 0 calc(${theme.size?.tabs.indicatorHeight} * -1) 0px ${String(
                  theme.palette.border?.subtle
                )}`,
                width: "100%",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: theme.palette.primary.main,
                height: theme.size?.tabs.indicatorHeight,
                borderRadius: 0,
                top: "auto", // Override the global top: 0
              },
            },
          },
        ],
      }),
      indicator: ({ theme }) => ({
        top: 0,
        bottom: 0,
        height: "auto",
        borderRadius: theme.borderRadius?.xLarge,
        zIndex: 1,
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        // to show it above the tab indicator
        zIndex: 2,
        textTransform: "none",
        padding: `${theme.spacing(1)} ${theme.spacing(5)}`,
        flexDirection: "row",
        lineHeight: theme.size?.tabs.lineHeight,
        minHeight: theme.size?.tabs.height,
        whiteSpace: "nowrap",
        maxWidth: "none", // Remove the default max-width constraint
        width: "auto", // Make tab width dynamic based on content

        "&.Mui-selected": {
          color: theme.palette.text?.invertPrimary,
        },

        variants: [
          {
            props: { appearance: "pill" },
            style: {
              padding: `${theme.spacing(0)} ${theme.spacing(5)}`,
              border: `1px solid ${String(theme.palette.border?.primary)}`,
              minWidth: "auto", // Remove minimum width
              flexShrink: 0, // Prevent tabs from shrinking
              "& .MuiBadge-badge": {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.text.invertPrimary,
                marginRight: 1,
              },
              "&.Mui-selected": {
                "& .MuiTypography-root": {
                  color: theme.palette.text.invertPrimary,
                },
                "&.MuiButtonBase-root": {
                  border: `1px solid ${theme.palette.primary.main}`,
                },
              },
            },
          },
          {
            props: { appearance: "underlined" },
            style: {
              padding: `${theme.spacing(3)} ${theme.spacing(6)}`,
              textTransform: "none",
              color: theme.palette.text.tertiary,
              flexGrow: 1,
              flexShrink: 0,
              "&.Mui-selected": {
                color: theme.palette.text.primary,
              },
              "&:hover": {
                color: theme.palette.text.secondary,
              },
            },
          },
        ],
      }),
    },
  },
};
